import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  input,
  OnInit,
  signal,
} from '@angular/core';
import { EditBeneficiaryFormModel } from './form-group/edit-beneficiary-form.model';
import { BaseFormComponent } from '../../core/components/base-form.component';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { EditBeneficiaryFormBuilder } from './form-group/edit-beneficiary.form.builder';
import { Beneficjent } from '../../shared/models/beneficjent';
import { Router } from '@angular/router';
import { UserApi } from '../../shared/services/api/user.api';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { Card } from 'primeng/card';
import { FormBeneficiaryDetailsComponent } from '../../shared/components/beneficiary/form-beneficiary-details/form-beneficiary-details.component';
import { FormBeneficiaryContactDetailsComponent } from '../../shared/components/beneficiary/form-contact-details/form-beneficiary-contact-details.component';
import { FormBankAccountComponent } from '../../shared/components/bank-account-number/form-bank-account/form-bank-account.component';
import {
  catchError,
  EMPTY,
  finalize,
  Observable,
  of,
  switchMap,
  tap,
} from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Button } from 'primeng/button';
import { FormEditBeneficiaryAddressComponent } from './components/form-edit-beneficiary-address/form-edit-beneficiary-address.component';
import { MessageService } from 'primeng/api';
import { SpinnerService } from '../../core/services/spinner.service';
import { ToastSeverity } from '../../shared/enums/toast-severity.enum';
import { APP_PATHS } from '../../core/config/app-paths';
import { EmailVerifyInfo } from '../../shared/components/email-verify-info/email-verify-info';
import { InfoMessageComponent } from '../../shared/components/info-message/info-message.component';
import { HttpResponseService } from '../../core/services/http-error-response.service';
import { FormsUtils } from '../../shared/utils/forms.utils';
import { UserEmailValidPipe } from '../../shared/components/verify-user-data-dialog/user-email-valid-pipe';

@Component({
  selector: 'app-beneficiary-user',
  imports: [
    ReactiveFormsModule,
    AutoCompleteModule,
    Card,
    FormBeneficiaryDetailsComponent,
    FormBeneficiaryContactDetailsComponent,
    FormBankAccountComponent,
    Button,
    FormEditBeneficiaryAddressComponent,
    EmailVerifyInfo,
    InfoMessageComponent,
    UserEmailValidPipe,
  ],
  templateUrl: './edit-beneficiary.component.html',
  styleUrl: './edit-beneficiary.component.scss',
  providers: [UserApi],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EditBeneficiaryComponent
  extends BaseFormComponent<EditBeneficiaryFormModel>
  implements OnInit
{
  readonly #destroyRef = inject(DestroyRef);
  readonly #userApi = inject(UserApi);
  readonly #messageService = inject(MessageService);
  readonly #httpResponseService = inject(HttpResponseService);
  readonly #spinnerService = inject(SpinnerService);
  readonly #router = inject(Router);
  protected user = input<Beneficjent | undefined>();
  protected readonly isSaving = signal(false);
  #emailOrigin = '';

  protected override createFormGroup(): FormGroup<EditBeneficiaryFormModel> {
    const fg = EditBeneficiaryFormBuilder.createFormGroup(this.user()!);
    this.#subscribeToAddressFgControls(fg);
    this.#subscribeToIsBankAccountControl(fg);
    EditBeneficiaryFormBuilder.setAvailabilityOfBankAccount(fg, true);
    this.#emailOrigin = this.user()?.email ?? '';
    return fg;
  }

  override ngOnInit(): void {
    super.ngOnInit();
    this.#markAllControlsAsTouchedIfUserDataNotVerified();
  }

  #subscribeToAddressFgControls(
    formGroup: FormGroup<EditBeneficiaryFormModel>,
  ): void {
    formGroup.controls.address.controls.adres.controls.czyAdresZagraniczny.valueChanges
      .pipe(
        tap(() =>
          EditBeneficiaryFormBuilder.handleAvailabilityOfForeignBankAccount(
            this.formGroup(),
          ),
        ),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe();
  }

  #subscribeToIsBankAccountControl(
    formGroup: FormGroup<EditBeneficiaryFormModel>,
  ): void {
    formGroup?.controls.kontoBankowe.controls.isBankAccount.valueChanges
      .pipe(
        tap(() => {
          EditBeneficiaryFormBuilder.setAvailabilityOfBankAccount(
            formGroup,
            false,
          );
          if (!formGroup?.controls.kontoBankowe.controls.isBankAccount.value) {
            this.#resetBankAccountData(formGroup);
          }
        }),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe();
  }

  #resetBankAccountData(formGroup: FormGroup<EditBeneficiaryFormModel>): void {
    formGroup?.controls.kontoBankowe.controls.isForeign.reset();
    formGroup?.controls.kontoBankowe.controls.domesticAccount.reset();
    formGroup?.controls.kontoBankowe.controls.foreignAccount.reset();
  }

  protected save(): void {
    if (this.isSaving()) {
      return;
    }
    if (!FormsUtils.isReadyForSave(this.formGroup())) {
      this.#messageService.add({
        severity: ToastSeverity.ERROR,
        summary: 'Wystąpił błąd podczas walidacji formularza',
      });
      return;
    }
    const beneficjent = EditBeneficiaryFormBuilder.toProfileModel(this.formGroup(), this.user()!);
    const emailChanged =
      this.#emailOrigin.toLowerCase()?.trim() !==
      beneficjent.email?.toLowerCase()?.trim();
    this.isSaving.set(true);
    this.#spinnerService.show();
    this.#userApi
      .saveUser$(beneficjent)
      .pipe(
        catchError((err) => {
          this.#httpResponseService.showErrorMessage(
            err,
            'Wystąpił błąd podczas zapisu ustawień',
          );
          return EMPTY;
        }),
        tap(() => {
          this.#emailOrigin = beneficjent.email ?? '';
          this.#httpResponseService.showSuccessMessage(
            'Poprawnie zapisano ustawienia',
          );
        }),
        // Odpowiedź zapisu oznacza zatwierdzoną transakcję; błąd SMTP nie cofnie danych.
        switchMap(() =>
          emailChanged ? this.#sendVerificationLink$() : of(undefined),
        ),
        tap(() => this.#router.navigate([APP_PATHS.userSettings.root])),
        takeUntilDestroyed(this.#destroyRef),
        finalize(() => {
          this.isSaving.set(false);
          this.#spinnerService.hide();
        }),
      )
      .subscribe();
  }

  #sendVerificationLink$(): Observable<void> {
    return this.#userApi.verifyEmail$().pipe(
      tap(() => {
        this.#messageService.add({
          severity: ToastSeverity.WARN,
          life: 8500,
          summary:
            'Twój adres e-mail wymaga weryfikacji. Link weryfikujący został wysłany.',
        });
      }),
      catchError(() => {
        this.#messageService.add({
          severity: ToastSeverity.WARN,
          sticky: true,
          summary:
            'Dane zostały zapisane, ale nie udało się wysłać linku weryfikującego adres e-mail. Spróbuj ponownie za kilka minut.',
        });
        return of(undefined);
      }),
    );
  }

  navigateToUserSettings(): void {
    this.#router.navigate(['/user-settings']);
  }

  #markAllControlsAsTouchedIfUserDataNotVerified(): void {
    if (!this.user()?.zweryfikowaneDane) {
      FormsUtils.markAllControlsAsTouchedAndDirty(
        Object.values(this.formGroup().controls),
      );
    }
  }
}
